'use client';

import { useState } from 'react';
import { BookOpen, ChevronDown, ChevronUp, Heart, Lightbulb, Target, Users, Info } from 'lucide-react';

export default function ParentGuidance() {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div
      style={{
        background: 'rgba(255, 255, 255, 0.98)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        borderRadius: '32px',
        padding: 'clamp(2rem, 4vw, 3rem)',
        marginBottom: 'clamp(3rem, 5vw, 4rem)',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(255, 255, 255, 0.5) inset',
        border: '2px solid rgba(255, 255, 255, 0.8)',
      }}
    >
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        style={{
          width: '100%',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: 0,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'clamp(1rem, 3vw, 1.25rem)' }}>
          <div
            style={{
              width: 'clamp(64px, 10vw, 80px)',
              height: 'clamp(64px, 10vw, 80px)',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #EC4899 0%, #DB2777 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 8px 24px rgba(236, 72, 153, 0.4), 0 0 0 3px rgba(255, 255, 255, 0.8)',
              animation: 'pulse 3s ease-in-out infinite',
            }}
          >
            <Heart size={32} color="white" strokeWidth={2.5} fill="white" />
          </div>
          <div style={{ textAlign: 'left' }}>
            <h3
              style={{
                fontSize: 'clamp(1.25rem, 4vw, 2rem)',
                fontWeight: 900,
                background: 'linear-gradient(135deg, #EC4899, #DB2777, #BE185D)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                margin: '0 0 clamp(0.25rem, 1vw, 0.375rem) 0',
                lineHeight: 1.2,
              }}
            >
              Parent & Teacher Guidance
            </h3>
            <p style={{ 
              fontSize: 'clamp(0.9375rem, 2vw, 1.0625rem)', 
              color: '#64748b', 
              margin: 0,
              fontWeight: 600,
              lineHeight: 1.5,
            }}>
              Montessori-aligned tips for supporting <span style={{ color: '#EC4899', fontWeight: 800 }}>bilingual learning</span>
            </p>
          </div>
        </div>
        <div
          style={{
            width: 'clamp(48px, 8vw, 56px)',
            height: 'clamp(48px, 8vw, 56px)',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #FFF5EB, #FEF3E2)',
            border: '2px solid rgba(248, 200, 160, 0.4)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            boxShadow: '0 4px 16px rgba(248, 200, 160, 0.3)',
          }}
        >
          {isExpanded ? <ChevronUp size={28} color="#92400E" strokeWidth={3} /> : <ChevronDown size={28} color="#92400E" strokeWidth={3} />}
        </div>
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div style={{ marginTop: '32px' }}>
          {/* Grid of Guidance Cards */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '24px',
              marginBottom: '32px',
            }}
          >
            {/* Card 1: Montessori Principles - Enhanced */}
            <div
              style={{
                background: 'linear-gradient(135deg, #F0F9FF 0%, #E0F2FE 100%)',
                borderRadius: '24px',
                padding: 'clamp(1.5rem, 3vw, 2rem)',
                border: '2px solid rgba(168, 216, 230, 0.4)',
                boxShadow: '0 8px 32px rgba(168, 216, 230, 0.15)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
              className="guidance-card"
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    background: '#667eea',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Target size={20} color="white" />
                </div>
                <h4 style={{ fontSize: '1.125rem', fontWeight: 800, color: '#1a202c', margin: 0 }}>
                  Follow the Child
                </h4>
              </div>
              <p style={{ fontSize: '0.9375rem', color: '#64748b', lineHeight: 1.6, margin: 0 }}>
                Let your child choose stories that interest them. Montessori education is self-guided - the child's natural curiosity leads learning. Don't force completion; celebrate exploration.
              </p>
            </div>

            {/* Card 2: Bilingual Strategy - Enhanced */}
            <div
              style={{
                background: 'linear-gradient(135deg, #FFF5EB 0%, #FEF3E2 100%)',
                borderRadius: '24px',
                padding: 'clamp(1.5rem, 3vw, 2rem)',
                border: '2px solid rgba(248, 200, 160, 0.4)',
                boxShadow: '0 8px 32px rgba(248, 200, 160, 0.15)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
              className="guidance-card"
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    background: '#ec4899',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Users size={20} color="white" />
                </div>
                <h4 style={{ fontSize: '1.125rem', fontWeight: 800, color: '#1a202c', margin: 0 }}>
                  One Person, One Language
                </h4>
              </div>
              <p style={{ fontSize: '0.9375rem', color: '#64748b', lineHeight: 1.6, margin: 0 }}>
                For best results, try having one parent consistently use one language, the other use the second language. This helps children differentiate languages naturally and builds consistent exposure.
              </p>
            </div>

            {/* Card 3: Repetition & Routine - Enhanced */}
            <div
              style={{
                background: 'linear-gradient(135deg, #F0FDF4 0%, #DCFCE7 100%)',
                borderRadius: '24px',
                padding: 'clamp(1.5rem, 3vw, 2rem)',
                border: '2px solid rgba(134, 239, 172, 0.4)',
                boxShadow: '0 8px 32px rgba(134, 239, 172, 0.15)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
              className="guidance-card"
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    background: '#10b981',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <BookOpen size={20} color="white" />
                </div>
                <h4 style={{ fontSize: '1.125rem', fontWeight: 800, color: '#1a202c', margin: 0 }}>
                  Embrace Repetition
                </h4>
              </div>
              <p style={{ fontSize: '0.9375rem', color: '#64748b', lineHeight: 1.6, margin: 0 }}>
                Children love and need repetition! Reading the same story 20 times is perfectly normal and beneficial. Each reading deepens understanding and builds vocabulary in both languages.
              </p>
            </div>
          </div>

          {/* Additional Tips */}
          <div
            style={{
              background: '#f8fafc',
              borderRadius: '16px',
              padding: '24px',
              marginBottom: '24px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <Lightbulb size={24} color="#667eea" />
              <h4 style={{ fontSize: '1.125rem', fontWeight: 800, color: '#1a202c', margin: 0 }}>
                Key Montessori Practices
              </h4>
            </div>
            <ul style={{ margin: 0, paddingLeft: '20px', color: '#64748b', fontSize: '0.9375rem', lineHeight: 1.8 }}>
              <li>
                <strong>Three-Period Lesson:</strong> When introducing new words, use: 1) "This is a tree" (naming), 2) "Show me the tree" (recognition), 3) "What is this?" (recall).
              </li>
              <li>
                <strong>Real-World Connection:</strong> After a story about nature, go outside and observe. Link stories to real experiences for deeper learning.
              </li>
              <li>
                <strong>Dialogic Reading:</strong> Ask questions during stories: "What do you see? What do you think will happen?" Encourage active participation, not passive listening.
              </li>
              <li>
                <strong>Intrinsic Motivation:</strong> Avoid rewards like stickers or prizes. The joy of the story itself and your presence are the rewards. Praise effort and curiosity, not just completion.
              </li>
              <li>
                <strong>Prepared Environment:</strong> Create a cozy reading space. Make bilingual books accessible. Let your child choose freely from available stories.
              </li>
              <li>
                <strong>Screen Time Balance:</strong> Use these stories as quality screen time, but always pair with offline activities. Montessori values hands-on learning above all.
              </li>
            </ul>
          </div>

          {/* Language Mixing Note */}
          <div
            style={{
              background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.1) 0%, rgba(245, 158, 11, 0.1) 100%)',
              borderRadius: '16px',
              padding: '20px',
              border: '2px solid rgba(251, 191, 36, 0.3)',
            }}
          >
            <h4 style={{ fontSize: '1rem', fontWeight: 800, color: '#1a202c', margin: '0 0 8px 0', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <div style={{
                background: 'linear-gradient(135deg, #3B82F6, #2563EB)',
                borderRadius: '50%',
                width: '24px',
                height: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <Info size={14} color="white" strokeWidth={2.5} />
              </div>
              Is Language Mixing Normal?
            </h4>
            <p style={{ fontSize: '0.9375rem', color: '#64748b', lineHeight: 1.6, margin: 0 }}>
              Yes! Young bilingual children often mix languages in one sentence (code-mixing). This is a normal developmental stage and shows they understand both languages. Don't correct harshly - simply respond in the target language. By age 4-5, most children naturally separate the languages.
            </p>
          </div>

          {/* Quote */}
          <div style={{ marginTop: '32px', textAlign: 'center' }}>
            <blockquote
              style={{
                fontSize: '1.125rem',
                fontStyle: 'italic',
                color: '#667eea',
                fontWeight: 600,
                margin: '0',
                lineHeight: 1.6,
              }}
            >
              "The greatest sign of success for a teacher is to be able to say, 'The children are now working as if I did not exist.'"
            </blockquote>
            <p style={{ fontSize: '0.875rem', color: '#64748b', marginTop: '8px' }}>
              — Dr. Maria Montessori
            </p>
          </div>
        </div>
      )}

      <style jsx global>{`
        .guidance-card:hover {
          transform: translateY(-4px) scale(1.02);
          box-shadow: 0 16px 48px rgba(0, 0, 0, 0.18) !important;
        }
        
        @keyframes pulse {
          0%, 100% {
            transform: scale(1);
            opacity: 1;
          }
          50% {
            transform: scale(1.05);
            opacity: 0.9;
          }
        }
        
        @media (max-width: 768px) {
          .guidance-card {
            padding: clamp(1.25rem, 3vw, 1.5rem) !important;
          }
        }
      `}</style>
    </div>
  );
}

